<template>
  <div
    style="padding-bottom: 60px"
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      {
        expanded: !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered),
      },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light',
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <span class="brand-logo">
              <b-img
                :src="isVerticalMenuCollapsed && !isMouseHovered ? collapsedImgUrl : imgUrl"
                alt="fbr invoices logo"
                :class="{ 'collapsed-logo': isVerticalMenuCollapsed && !isMouseHovered }"
                :style="
                  isVerticalMenuCollapsed && !isMouseHovered
                    ? collapsedLogoStyle
                    : { maxWidth: '120px', padding: '10px' }
                "
              />
            </span>
            <!-- <b-link class="navbar-brand" to="/apps/fbr-invoices">
              <span class="brand-logo">
                <b-img
                  :src="isVerticalMenuCollapsed && !isMouseHovered ? collapsedImgUrl : imgUrl"
                  alt="fbr invoices logo"
                  :class="{ 'collapsed-logo': isVerticalMenuCollapsed && !isMouseHovered }"
                  :style="
                    isVerticalMenuCollapsed && !isMouseHovered
                      ? collapsedLogoStyle
                      : { maxWidth: '120px', padding: '10px' }
                  "
                />
              </span>
            </b-link> -->
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div :class="{ 'd-block': shallShadowBottom }" class="shadow-bottom" />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="
        (evt) => {
          shallShadowBottom = evt.srcElement.scrollTop > 0;
        }
      "
    >
      <!-- navMenuItems-->
      <vertical-nav-menu-items
        :items="filteredNavigationItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import navMenuItems from '@/navigation/vertical';
import useAppConfig from '@core/app-config/useAppConfig';
import { $themeConfig } from '@themeConfig';
import { computed, provide, ref } from '@vue/composition-api';
import { BImg, BLink } from 'bootstrap-vue';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue';
import useVerticalNavMenu from './useVerticalNavMenu';

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props);

    const { skin } = useAppConfig();

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false);

    provide('isMouseHovered', isMouseHovered);

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    const collapseTogglerIconFeather = computed(() =>
      collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'
    );

    // App Name
    const { appName, appLogoImage } = $themeConfig.app;

    const collapsedImgUrl = require('@/assets/images/logo/fbr-fav.png');
    const imgUrl = require('@/assets/images/logo/fbr-logo-black.png');

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
      collapsedImgUrl,
      imgUrl,
    };
  },
  computed: {
    // Filter navigation items based on user permissions
    filteredNavigationItems() {
      const navigationItems = this.$store.state.user.userRights;
      const isAdmin = this.$store.getters['user/isAdmin'];

      // Create a copy of navigation items to avoid modifying the original
      let filteredItems = [...navigationItems];

      // Boolean flag to show/hide Reports section
      const showReportsSection = false; // Set to true to enable Reports section

      // Add Reports section for all users (only if enabled)
      if (showReportsSection) {
        const reportsItem = {
          name: 'Reports',
          icon: 'BarChartIcon',
          children: [
            {
              title: 'Customer Report',
              route: 'fbr-customer-report',
            },
            {
              title: 'Items Report',
              route: 'fbr-items-report',
            },
            // {
            //   title: 'Sales Invoice Report',
            //   route: 'fbr-sales-invoice-report',
            // },
            {
              title: 'Day End Closing Report',
              route: 'fbr-sales-invoice-summary-report',
            },
          ],
        };

        // Find User Management index and insert Reports after it
        const userManagementIndex = filteredItems.findIndex(
          (item) => item.title === 'User Management'
        );

        if (userManagementIndex !== -1) {
          filteredItems.splice(userManagementIndex + 1, 0, reportsItem);
        } else {
          // If User Management not found, add at the end
          filteredItems.push(reportsItem);
        }
      }

      // If user is admin, add Business Management after Reports (or at the end if Reports is hidden)
      if (isAdmin) {
        const businessManagementItem = {
          title: 'Business Management',
          icon: 'BriefcaseIcon',
          route: 'fbr-business-management',
        };

        if (showReportsSection) {
          // Find Reports index and insert Business Management after it
          const reportsIndex = filteredItems.findIndex(
            (item) => item.name === 'Reports'
          );

          if (reportsIndex !== -1) {
            filteredItems.splice(reportsIndex + 1, 0, businessManagementItem);
          } else {
            // If Reports not found, add at the end
            filteredItems.push(businessManagementItem);
          }
        } else {
          // If Reports section is hidden, add Business Management at the end
          filteredItems.push(businessManagementItem);
        }
      }

      return filteredItems;
    },
    // imgUrl() {
    //   if (this.$store.state.appConfig.layout.skin === 'dark') {
    //     let image = require('@/assets/images/logo/fbr-logo-black.png');
    //     return image;
    //   }
    //   return require('@/assets/images/logo/fbr-fav.png');
    // },
    // collapsedLogoStyle() {
    //   return require('@/assets/images/logo/fbr-fav.png');
    // },
    collapsedLogoStyle() {
      return {
        width: '60px',
        height: '60px',
        paddingTop: '6px',
        margin: '0 auto',
        display: 'block',
      };
    },
  },
};
</script>

<style lang="scss">
@import '~@core/scss/base/core/menu/menu-types/vertical-menu.scss';
</style>

<style scoped lang="css">
.navbar-header {
  height: 5.5rem;
}

.collapsed-logo {
  transition: all 0.3s ease;
  width: 40px !important;
  object-fit: contain;
}
</style>
